import { useDebounce } from '@/hooks/use-debounce';
import { useGetOwnTemplatesForOrgPaginated } from '@/modules/templates/hooks/use-templates';
import type { Template } from '@/modules/templates/types/templates.types';
import { useState } from 'react';

export const ORG_TEMPLATES_PAGE_SIZE = 10;
export const ORG_TEMPLATES_QUERY_KEY = ['ownTemplatesForOrgPaginated'];

export interface OrgTemplatesController {
  searchInput: string;
  onSearchChange: (value: string) => void;
  page: number;
  setPage: (page: number) => void;
  templates: Template[];
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
  /** True when the tab is empty because the org has no templates (not a search miss). */
  isEmptyState: boolean;
}

/**
 * Owns the Org Templates tab state (search, pagination) and its paginated query.
 * Shared by the tab's toolbar and its list so both read one source of truth.
 */
export const useOrgTemplates = (orgId?: string | null): OrgTemplatesController => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);

  const { data, isLoading } = useGetOwnTemplatesForOrgPaginated({
    orgId,
    pageNo: page,
    pageSize: ORG_TEMPLATES_PAGE_SIZE,
    search,
  });

  const templates = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  return {
    searchInput,
    onSearchChange: (value) => {
      setSearchInput(value);
      setPage(1);
    },
    page,
    setPage,
    templates,
    totalCount,
    totalPages: data?.totalPages ?? 1,
    isLoading,
    isEmptyState: !isLoading && templates.length === 0 && !search,
  };
};
